import {
  Aggregate,
  AggregateColumnDirective,
  AggregateColumnsDirective,
  AggregateDirective,
  AggregatesDirective,
  ColumnChooser,
  ColumnDirective,
  ColumnsDirective,
  Edit,
  ExcelExport,
  Filter,
  Freeze,
  GridComponent,
  Group,
  Inject,
  Page,
  Reorder,
  Resize,
  RowDD,
  Search,
  Sort,
  Toolbar,
  VirtualScroll
} from '@syncfusion/ej2-react-grids';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { isEqual, omit } from 'lodash';
import { FC, Key, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Spinner, UncontrolledTooltip } from 'reactstrap';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { appIntl } from '../../../utility/context/IntlGlobalProvider';
// ** Internationalization Context
import * as authSelectors from '@redux/reducers/auth';
import { L10n, setCulture } from '@syncfusion/ej2-base';
import { ToolbarItems } from '@syncfusion/ej2-react-grids';
import { TableColumn } from 'utility/types';
import { IntlContext } from '../../../utility/context/Internationalization';

const SELECTION_PRESERVE_ACTIONS = new Set([
  'paging',
  'sorting',
  'filtering',
  'searching',
  'grouping',
  'ungrouping'
]);
interface SortedColumn {
  sortedColumnIndex: number;
  direction: 'Ascending' | 'Descending';
}

interface Props {
  setTableRef?: (value: GridComponent | null) => void;
  id?: string;
  version?: number;
  enablePersistence?: boolean;
  toolbarOptions?: ToolbarItems[];
  toolbarClick?: any;
  omitPersistenceKeys?: string[];
  enableVirtualization?: boolean;
  rerender?: Date | Key;
  selectAll?: Date | Key;
  deselectAll?: Date | Key;
  columns: TableColumn[];
  initialFilterColumns?: any[];
  sortSettings?: any;
  sortedColumn?: SortedColumn;
  data: any[];
  actionSave?: (value: any) => any;
  onDrop?: Function;
  onSelect?: Function;
  onDeselect?: Function;
  selected?: any;
  changeSelected?: Function;
  isFetching?: boolean;
  allowSorting?: boolean;
  allowRowDragAndDrop?: boolean;
  allowPaging?: boolean;
  pageSize?: number;
  allowResizing?: boolean;
  allowReordering?: boolean;
  allowFiltering?: boolean;
  allowMultipleSelection?: boolean;
  allowExcelExport?: boolean;
  initialSelectedRowIndex?: number;
  frozenColumns?: number;
  height?: number;
  allowGrouping?: boolean;
  groupSettings?: any;
  allowDeleting?: boolean;
  usingAdvancedFilters?: boolean;
  sharedLink?: boolean;
  disableFilterFields?: String[];
  useDynamicColumns?: boolean;
  compact?: boolean;
  primaryKey?: string[]; // Array de nombres de campos que forman la clave primaria para identificar filas de manera única
  persistSelection?: boolean; // Habilitar persistencia nativa de Syncfusion para selecciones
}

const Table: FC<Props> = (props) => {
  const { isFetching } = props;

  return !isFetching ? (
    <TableComponent {...props} />
  ) : (
    <Row>
      <Col sm="12" className="grid-spinner">
        <Spinner color="primary" size="lg" />
      </Col>
    </Row>
  );
};

const TableComponent = (props: Props) => {
  const {
    id,
    version,
    enablePersistence,
    omitPersistenceKeys,
    enableVirtualization,
    columns,
    rerender,
    selectAll,
    deselectAll,
    initialFilterColumns,
    sortSettings = undefined,
    data,
    actionSave,
    onDrop,
    onSelect,
    onDeselect,
    selected,
    changeSelected,
    allowSorting,
    allowRowDragAndDrop,
    allowPaging,
    pageSize,
    allowResizing,
    allowReordering,
    allowFiltering,
    allowMultipleSelection,
    sortedColumn,
    frozenColumns,
    initialSelectedRowIndex,
    height,
    toolbarOptions,
    allowExcelExport,
    toolbarClick,
    setTableRef,
    allowGrouping,
    groupSettings,
    usingAdvancedFilters = false,
    sharedLink = false,
    disableFilterFields,
    useDynamicColumns = false,
    compact = false,
    primaryKey,
    persistSelection = false
  } = props;

  const [state, changeState] = useState(Object);
  const [initialDataBound, setInitialDataBound] = useState(false);
  const [show, setShow] = useState<boolean>(true);
  const [locale, setLocale] = useState<any>();
  const stateRef = useRef(state);

  /**
   * Key must be a React.Key type
   * when its a date, javascript automatically converts to key
   */
  const [key, changeKey] = useState<Date | Key>(new Date());
  const [tooltip, setTooltip] = useState<TooltipComponent | null>(null);
  const [headerTooltips, setHeaderTooltips] = useState<{ id: string; text: string }[]>([]); // Lista de tooltips para headers
  const [tableColumns, setTableColumns] = useState<any[]>([]);
  const [scrollLeft, setScrollLeft] = useState<any>(null);
  // Estado interno para persistir IDs de filas seleccionadas (en lugar de índices)
  // Esta funcionalidad mantiene las selecciones después de re-renders del grid
  // usando IDs únicos en lugar de índices para evitar problemas cuando cambian los datos
  // sin exponer ningún prop externo - es completamente interna al componente
  const [persistedRowIds, setPersistedRowIds] = useState<any[]>([]);
  const ongoingActionRef = useRef<string | null>(null);
  const selectedRowIdsRef = useRef<string[]>([]);
  const intlContext = useContext(IntlContext);
  const isMounted = useRef(true);
  const previousDataLength = useRef<number>(0);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(selected) || selected.length === 0) {
      selectedRowIdsRef.current = [];
      return;
    }

    const derivedIds = selected
      .map((row, index) => {
        if (row?.dataRowTableId !== undefined && row?.dataRowTableId !== null) {
          return row.dataRowTableId;
        }
        if (row?.id !== undefined && row?.id !== null) {
          return row.id;
        }
        if (row?.key !== undefined && row?.key !== null) {
          return row.key;
        }
        return primaryKey && primaryKey.length > 0 ? generateStableId(row, index) : null;
      })
      .filter((id) => id !== null && id !== undefined)
      .map((id) => String(id));

    selectedRowIdsRef.current = derivedIds;
  }, [selected, primaryKey]);

  // Función helper para generar un ID estable usando la clave primaria
  const generateStableId = (row: any, index: number): string => {
    if (primaryKey && primaryKey.length > 0) {
      // Usar los campos especificados en primaryKey para crear un ID único
      const keyValues = primaryKey
        .map((key) => {
          const value = row[key];
          // Manejar valores null/undefined/empty string
          return value !== null && value !== undefined && value !== ''
            ? String(value)
            : `null_${key}`;
        })
        .join('|'); // Usar separador que sea difícil que aparezca en los datos

      return `pk_${keyValues}`;
    }

    // Fallback a la solución actual si no hay primaryKey
    return (
      row.id ||
      row.key ||
      row.dataRowTableId ||
      `row_${JSON.stringify(row).substring(0, 50)}_${index}`
    );
  };

  // Memoizar las transformaciones de columnas para evitar recálculos innecesarios
  const columnTransformations = useMemo(() => {
    if (tableColumns.length === 0) return { dateTimeColumns: [], numberColumns: [] };

    return {
      dateTimeColumns: tableColumns.filter((col) => col?.type === 'dateTime'),
      numberColumns: tableColumns.filter((col) => col?.type === 'number')
    };
  }, [tableColumns]);

  // Memoizar dataTable con dependencias optimizadas
  const dataTable = useMemo(() => {
    // Si no hay datos, retornar array vacío sin procesar
    if (!data || data.length === 0) {
      return [];
    }

    // Solo procesar si tenemos tanto datos como columnas, o si es la primera carga
    if (tableColumns.length === 0) {
      // Retornar datos con ID estable hasta que las columnas estén listas
      return data.map((row, i) => ({
        ...row,
        dataRowTableId: generateStableId(row, i)
      }));
    }

    const { dateTimeColumns, numberColumns } = columnTransformations;

    return data.map((row, i) => ({
      ...row,
      // Usar la función helper para generar un ID estable
      dataRowTableId: generateStableId(row, i),
      // Transformar campos dateTime con validación para evitar Invalid Date
      ...dateTimeColumns.reduce((obj, item) => {
        const value = row[item.fieldName];
        let dateValue = null;

        // Solo procesar si hay un valor válido
        if (value) {
          const parsedDate = new Date(value);
          // Verificar que la fecha sea válida antes de asignarla
          dateValue = !isNaN(parsedDate.getTime()) ? parsedDate : null;
        }

        return Object.assign(obj, {
          [item.fieldName]: dateValue
        });
      }, {}),
      // Transformar campos number con validación para evitar NaN
      ...numberColumns.reduce((obj, item) => {
        const value = row[item.fieldName];
        let numberValue = null;

        // Solo procesar valores no nulos y no vacíos
        if (value != null && value !== '') {
          const parsedNumber = Number(value);
          // Verificar que sea un número válido y finito
          numberValue = !isNaN(parsedNumber) && isFinite(parsedNumber) ? parsedNumber : null;
        }

        return Object.assign(obj, {
          [item.fieldName]: numberValue
        });
      }, {})
    }));
  }, [data, columnTransformations, primaryKey]);

  const { user, authSystems } = useSelector((state) => ({
    user: authSelectors?.getAuthUser(state),
    authSystems: authSelectors?.getAuthSystems(state)
  }));

  const currentLocale = intlContext?.locale === 'es' ? 'es-US' : intlContext?.locale || 'es-US';

  setCulture(currentLocale);

  const notFoundMessage = usingAdvancedFilters
    ? appIntl()?.formatMessage({ id: 'common.noRecordsTryAdvancedFilters' })
    : appIntl()?.formatMessage({ id: 'common.noRecordsToDisplay' });

  L10n.load({
    [currentLocale]: {
      grid: {
        EmptyRecord: notFoundMessage // Set the dynamic "No Records Found" message
      }
    }
  });
  const handleActionBegin = (args) => {
    try {
      if (args && typeof args.requestType === 'string') {
        if (SELECTION_PRESERVE_ACTIONS.has(args.requestType)) {
          ongoingActionRef.current = args.requestType;
        }
      }
    } catch (error) {
      // Silently handle action begin tracking errors
    }
  };
  const handleActionComplete = (args) => {
    if (args.requestType === 'reorder') {
      if (stateRef.current) {
        const newColumnsOrder = stateRef.current.getColumns().map((col) => {
          return tableColumns.find((c) => c.fieldName === col.field);
        });
        setTableColumns(newColumnsOrder.filter((col) => col));
      }
    }

    // Reaplica autoFit después de acciones que pueden cambiar el layout de la tabla
    if (
      args.requestType === 'sorting' ||
      args.requestType === 'paging' ||
      args.requestType === 'filtering' ||
      args.requestType === 'searching' ||
      args.requestType === 'grouping' ||
      args.requestType === 'ungrouping'
    ) {
      // Usar requestAnimationFrame para sincronizar con el siguiente frame de renderizado
      requestAnimationFrame(() => {
        // Esperar a que el DOM se actualice completamente
        setTimeout(() => {
          if (stateRef.current?.autoFitColumns && isMounted.current) {
            try {
              const gridElement = stateRef.current.element;
              if (gridElement) {
                // Aplicar clase CSS para transición suave
                gridElement.classList.add('grid-smooth-resize');

                // Aplicar autoFit con un pequeño delay adicional para suavidad
                setTimeout(() => {
                  if (stateRef.current?.autoFitColumns && isMounted.current) {
                    stateRef.current.autoFitColumns();
                  }
                }, 10);

                // Remover la clase después de que termine la transición
                setTimeout(() => {
                  if (gridElement && isMounted.current) {
                    gridElement.classList.remove('grid-smooth-resize');
                  }
                }, 350); // Un poco más que la duración de la transición CSS (300ms)
              } else {
                // Fallback: aplicar autoFit normal si no hay elemento
                stateRef.current.autoFitColumns();
              }
            } catch (error) {
              // Fallback: aplicar autoFit normal en caso de error
              if (stateRef.current?.autoFitColumns) {
                stateRef.current.autoFitColumns();
              }
            }
          }
        }, 100); // Delay optimizado para balance entre velocidad y suavidad
      });
    }

    ongoingActionRef.current = null;
  };

  useEffect(() => {
    //Elimina del localStorage las keys que se quieren eliminar
    try {
      if (id && enablePersistence && omitPersistenceKeys && omitPersistenceKeys?.length > 0) {
        const value = window?.localStorage?.getItem('grid' + id);
        if (value) {
          const savedVersion = parseInt(
            JSON?.parse(window?.localStorage?.getItem('grid' + id + '_version') || '0')
          );
          if ((version || 0) === savedVersion) {
            const model = omit(JSON?.parse(value), omitPersistenceKeys);
            window?.localStorage?.setItem('grid' + id, JSON?.stringify(model));
          } else {
            window?.localStorage?.removeItem('grid' + id);
            window?.localStorage?.setItem('grid' + id + '_version', JSON?.stringify(version));
          }
        }
      }
    } catch (error) {
      // Silently handle persistence errors
    }
  }, []);

  useEffect(() => {
    try {
      if (state) {
        stateRef.current = state;
      }
      // AutoFit solo cuando hay columnas disponibles para evitar ejecuciones innecesarias
      if (
        stateRef.current?.headerModule &&
        stateRef.current?.autoFitColumns &&
        tableColumns.length > 0
      ) {
        stateRef.current?.autoFitColumns();
      }
    } catch (error) {
      // Silently handle autoFit errors
    }
  }, [state, tableColumns.length]);

  useEffect(() => {
    //Se setean la columna que se quiere ordenar en la tabla
    try {
      if (
        stateRef.current?.headerModule &&
        stateRef.current?.sortSettings &&
        sortedColumn?.sortedColumnIndex &&
        tableColumns?.length > 0 &&
        (stateRef.current?.sortSettings?.columns?.length === 0 ||
          tableColumns[sortedColumn?.sortedColumnIndex]?.fieldName !==
            stateRef.current?.sortSettings?.columns[0]?.field ||
          sortedColumn.direction !== stateRef.current?.sortSettings?.columns[0]?.direction)
      ) {
        stateRef.current.sortSettings.columns = [
          {
            field: tableColumns[sortedColumn?.sortedColumnIndex]?.fieldName,
            direction: sortedColumn?.direction
          }
        ];
        reset();
      }
    } catch (error) {
      // Silently handle sort errors
    }
  }, [state?.sortSettings, tableColumns?.length, sortedColumn?.sortedColumnIndex]);

  useEffect(() => {
    //Se restringe dependiendo del nivel de acceso del usuario
    try {
      if (columns?.length > 0) {
        let newColumns = columns?.filter((column) => column !== undefined && column !== null);

        // Aplica los filtros según el nivel de acceso
        if (user?.accessLevelId == 11 || user?.accessLevelId == 13) {
          newColumns = newColumns.filter(
            (column) => column?.fieldName && !['operatorName']?.includes(column?.fieldName)
          );
        } else if (user?.accessLevelId == 7) {
          newColumns = newColumns.filter(
            (column) =>
              column?.fieldName &&
              !['operatorName', 'organizationName']?.includes(column?.fieldName)
          );
        } else if (user?.accessLevelId == 4) {
          newColumns = newColumns.filter(
            (column) =>
              column?.fieldName &&
              !['operatorName', 'organizationName', 'divisionName']?.includes(column?.fieldName)
          );
        } else if (user?.accessLevelId == 2 || user?.accessLevelId == 20) {
          newColumns = newColumns.filter(
            (column) =>
              column?.fieldName &&
              !['operatorName', 'organizationName', 'divisionName', 'subdivisionName']?.includes(
                column?.fieldName
              )
          );
        }

        if (!isEqual(tableColumns, newColumns)) {
          setTableColumns(newColumns);
        }
      }
    } catch (error) {
      // Silently handle access level column restrictions
    }
  }, [user?.accessLevelId, columns?.length]);

  useEffect(() => {
    if (columns?.length !== tableColumns?.length && useDynamicColumns) {
      if (!isEqual(tableColumns, columns)) {
        setTableColumns(columns);
      }
    }
  }, [columns?.length]);

  useEffect(() => {
    try {
      if (
        selected &&
        changeSelected &&
        dataTable?.length > 0 &&
        typeof stateRef.current?.getFilteredRecords === 'function'
      ) {
        if (stateRef.current?.selectRowsByRange)
          stateRef.current?.selectRowsByRange(0, dataTable?.length);
        var selectedRows: any[] = [];
        selectedRows = stateRef.current?.getFilteredRecords();
        if (selectedRows?.length === 0) {
          const input = document?.getElementById(`${id}_searchbar`);
          if (input) {
            selectedRows = dataTable?.filter((data) =>
              tableColumns
                ?.filter((column) => typeof data[column?.fieldName] === 'string')
                ?.map((column) =>
                  data[column?.fieldName]
                    ?.toLowerCase()
                    ?.trim()
                    ?.includes(input['value']?.toLowerCase()?.trim())
                )
                ?.includes(true)
            );
          } else {
            selectedRows = [
              ...selected,
              ...dataTable?.filter(
                (row, i) => !selected?.map((row) => row?.dataRowTableId)?.includes(i)
              )
            ];
          }
        }
        changeSelected(selectedRows);
      }
    } catch (error) {
      // Silently handle selectAll errors
    }
  }, [selectAll]);

  useEffect(() => {
    try {
      if (stateRef.current?.clearSelection) stateRef.current?.clearSelection();
      if (selected && changeSelected) {
        changeSelected([]);
      }
    } catch (error) {
      // Silently handle deselectAll errors
    }
  }, [deselectAll]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    try {
      if (locale) {
        setShow(false);
        timeout = setTimeout(() => {
          if (isMounted.current) setShow(true);
        }, 1000);
      } else {
        setLocale(intlContext?.locale);
      }
    } catch (error) {
      // Silently handle locale update errors
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [intlContext?.locale]);

  // Memoizar una representación estable de las columnas para evitar re-renders innecesarios
  const stableColumnDeps = useMemo(() => {
    return tableColumns?.length > 0
      ? tableColumns.map((col) => ({
          fieldName: col?.fieldName,
          headerText: col?.headerText,
          hide: col?.hide
        }))
      : [];
  }, [tableColumns]);

  useDeepCompareEffect(() => {
    try {
      // Solo hacer reset si hay cambios significativos en las columnas
      if (tableColumns.length > 0) {
        setNeedsRefresh(true);
        reset();

        // Solo usar rerender si es específicamente proporcionado
        if (rerender) {
          setTimeout(() => changeKey(rerender), 200);
        }
      }
    } catch (error) {
      // Silently handle rerender errors
    }
  }, [rerender, stableColumnDeps]);

  const reset = () => {
    let timeout: NodeJS.Timeout;

    // Capturar IDs de filas seleccionadas antes de hacer refresh
    try {
      if (stateRef.current?.selectionModule?.selectedRowIndexes?.length > 0) {
        // Obtener los IDs de las filas seleccionadas actualmente
        const selectedRowIds = stateRef.current.selectionModule.selectedRowIndexes
          .map((index) => {
            const rowData = stateRef.current?.getCurrentViewRecords?.()?.[index];
            return rowData?.dataRowTableId;
          })
          .filter((id) => id !== undefined);

        setPersistedRowIds(selectedRowIds);
      }
    } catch (error) {
      // Silently handle selection capture errors
    }

    if (scrollLeft) {
      timeout = setTimeout(() => {
        if (isMounted.current) {
          stateRef.current.scrollModule['content'].scrollLeft = scrollLeft;
          setScrollLeft(null);
        }
      }, 25);
    }

    // Solo hacer refresh si realmente es necesario
    if (stateRef.current?.refresh && needsRefresh) {
      stateRef.current.refresh();
    }

    // Solo limpiar selección si hay elementos seleccionados
    if (selected && selected.length > 0 && changeSelected && isMounted.current) {
      changeSelected([]);
    }

    return () => clearTimeout(timeout);
  };

  const select = (event: any) => {
    if (selected && changeSelected) {
      if (allowMultipleSelection) {
        // Selección múltiple - agregar elementos manteniendo los existentes
        if (Array?.isArray(event?.data)) {
          if (event?.data?.length > 0)
            changeSelected([
              ...selected,
              ...event?.data?.filter(
                (row) => !selected?.map((row) => row?.dataRowTableId)?.includes(row?.dataRowTableId)
              )
            ]);
          else
            changeSelected([
              ...selected,
              ...dataTable?.filter(
                (row, i) =>
                  event?.rowIndexes?.includes(i) &&
                  !selected?.map((row) => row?.dataRowTableId)?.includes(row?.dataRowTableId)
              )
            ]);
        } else if (event?.data === undefined)
          changeSelected([...selected, ...dataTable?.filter((row, i) => i === event?.rowIndex)]);
        else changeSelected([...selected, event?.data]);
      } else {
        // Selección simple - reemplazar la selección actual
        if (Array?.isArray(event?.data)) {
          if (event?.data?.length > 0) {
            changeSelected(event?.data);
          } else {
            const newSelection = dataTable?.filter((row, i) => event?.rowIndexes?.includes(i));
            changeSelected(newSelection || []);
          }
        } else if (event?.data === undefined) {
          const newSelection = dataTable?.filter((row, i) => i === event?.rowIndex);
          changeSelected(newSelection);
        } else {
          changeSelected([event?.data]);
        }
      }
      if (onSelect) onSelect(event);
    }
  };

  const deselect = (event: any) => {
    if (selected && changeSelected) {
      const shouldPreserveSelection =
        allowMultipleSelection &&
        ongoingActionRef.current &&
        event?.isInteracted === false &&
        SELECTION_PRESERVE_ACTIONS.has(ongoingActionRef.current);

      if (shouldPreserveSelection) {
        return;
      }

      if (allowMultipleSelection) {
        // Selección múltiple - remover solo los elementos deseleccionados
        if (Array?.isArray(event?.data))
          changeSelected(
            selected?.filter(
              (row) =>
                !event?.data?.map((row) => row?.dataRowTableId)?.includes(row?.dataRowTableId)
            )
          );
        else
          changeSelected(
            selected?.filter(
              (row) =>
                row?.dataRowTableId !==
                (event?.data?.dataRowTableId ?? dataTable[event?.rowIndex]?.dataRowTableId)
            )
          );
      } else {
        // Selección simple - limpiar toda la selección
        changeSelected([]);
      }
      if (onDeselect) onDeselect(event);
    }
  };

  // Función para generar los tooltips de headers
  const generateHeaderTooltips = (columns: TableColumn[]) => {
    const tooltips: { id: string; text: string }[] = [];

    const processColumns = (cols: TableColumn[], prefix = '') => {
      cols.forEach((column, index) => {
        if (column.headerTooltip) {
          const headerId = `${id || 'table'}-header-${prefix}${index}`;
          tooltips.push({
            id: headerId,
            text: column.headerTooltip
          });
        }

        // Procesar columnas stacked recursivamente
        if (column.columns && column.columns.length > 0) {
          processColumns(column.columns, `${prefix}${index}-`);
        }
      });
    };

    processColumns(columns);
    return tooltips;
  };

  const toolbar: ToolbarItems[] | undefined =
    toolbarOptions ??
    (tableColumns && tableColumns?.some((column) => column.allowEditing) ? undefined : ['Search']);

  // Generar tooltips cuando cambien las columnas
  useEffect(() => {
    if (tableColumns.length > 0) {
      const tooltips = generateHeaderTooltips(tableColumns);
      setHeaderTooltips(tooltips);
    }
  }, [tableColumns, id]);

  // Observar cuando la tabla Syncfusion está completamente renderizada
  useEffect(() => {
    if (headerTooltips.length > 0) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Verificar si algún elemento header fue agregado
            const hasHeaderElements = Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node as Element).querySelector('.e-headercelldiv')
            );

            if (hasHeaderElements) {
              // Pequeño delay para asegurar que los IDs estén establecidos
              setTimeout(() => {
                setHeaderTooltips([...headerTooltips]);
              }, 100);
            }
          }
        });
      });

      const gridElement = document.getElementById(id || 'table');
      if (gridElement) {
        observer.observe(gridElement, {
          childList: true,
          subtree: true
        });
      }

      return () => observer.disconnect();
    }
  }, [headerTooltips.length, id]);

  // Estado para controlar cuando necesitamos forzar re-render
  const [hasInitialData, setHasInitialData] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);

  // Efecto optimizado para manejar cambios en dataTable
  useEffect(() => {
    const hasData = dataTable && dataTable.length > 0;

    // Si es la primera vez que recibimos datos
    if (hasData && !hasInitialData) {
      setHasInitialData(true);
      setNeedsRefresh(true);
      changeKey(new Date().getTime());
      return;
    }

    // Si ya teníamos datos y cambiaron significativamente
    if (hasData && hasInitialData && needsRefresh) {
      setNeedsRefresh(false);
      if (stateRef.current && typeof stateRef.current.refresh === 'function') {
        stateRef.current.refresh();
      }
      return;
    }

    // Para cambios menores en los datos, capturar selección actual antes de que se pierda
    if (hasData && hasInitialData && !needsRefresh) {
      // Capturar IDs de filas seleccionadas antes de cualquier actualización
      try {
        if (stateRef.current?.selectionModule?.selectedRowIndexes?.length > 0) {
          // Obtener los IDs de las filas seleccionadas actualmente
          const selectedRowIds = stateRef.current.selectionModule.selectedRowIndexes
            .map((index) => {
              const rowData = stateRef.current?.getCurrentViewRecords?.()?.[index];
              return rowData?.dataRowTableId;
            })
            .filter((id) => id !== undefined);

          setPersistedRowIds(selectedRowIds);
        }
      } catch (error) {
        // Silently handle selection capture errors for minor updates
      }
    }
  }, [dataTable, hasInitialData, needsRefresh, state]);

  return show ? (
    <TooltipComponent
      target=".e-rowcell.hasTooltip" // Solo aplica a celdas marcadas con la clase hasTooltip
      position="TopCenter" // Posición del tooltip sobre la celda
      opensOn="Hover" // Se activa al hacer hover
      closeDelay={200} // Delay antes de cerrar (ms)
      openDelay={500} // Delay antes de abrir (ms) - evita tooltips accidentales
      ref={(t) => {
        if (t) {
          setTooltip(t); // Guardamos referencia para manipular el tooltip dinámicamente
        }
      }}
      beforeRender={(args) => {
        if (args && args.target && tooltip) {
          const target = args.target as HTMLElement; // Elemento donde se hizo hover
          const row = target.closest('tr') as HTMLTableRowElement; // Fila que contiene la celda

          if (row) {
            try {
              // Obtener el índice de la fila de manera más precisa
              let rowIndex = -1;

              // Método principal: usar aria-rowindex directamente (es más confiable)
              const ariaRowIndex = row.getAttribute('aria-rowindex');
              if (ariaRowIndex) {
                // aria-rowindex es 1-based pero no incluye header en nuestro caso
                rowIndex = parseInt(ariaRowIndex) - 1; // Convertimos a 0-based para acceder al array
              }

              // Fallback: contar filas manualmente si aria-rowindex no está disponible
              if (rowIndex < 0) {
                const tbody = row.parentElement;
                // Selecciona solo filas de datos, excluyendo el header (aria-rowindex="1")
                const allRows =
                  tbody?.querySelectorAll('tr[role="row"]:not([aria-rowindex="1"])') || [];

                // Busca la posición actual de la fila en la lista de filas de datos
                for (let i = 0; i < allRows.length; i++) {
                  if (allRows[i] === row) {
                    rowIndex = i;
                    break;
                  }
                }
              }

              // Último fallback: métodos anteriores
              if (rowIndex < 0) {
                // Usar data-rowindex del target si está disponible
                const dataRowIndex = target.getAttribute('data-rowindex');
                if (dataRowIndex) {
                  rowIndex = parseInt(dataRowIndex);
                }

                // Usar rowIndex de la tabla HTML como último recurso
                if (rowIndex < 0 && row.rowIndex > 0) {
                  rowIndex = row.rowIndex - 1; // Restamos 1 porque rowIndex incluye el header
                }
              }

              // Función para buscar la columna correcta (incluye soporte para columnas stacked)
              const findColumn = () => {
                // Obtener el aria-label que contiene información del campo
                const ariaLabel = target.getAttribute('aria-label') || '';

                // Función recursiva para buscar en columnas anidadas (stacked)
                const searchColumns = (columns: any[]): any => {
                  for (const column of columns) {
                    // Verificar si el aria-label contiene el fieldName de esta columna
                    if (column.fieldName && ariaLabel.includes(column.fieldName)) {
                      return column;
                    }

                    // Buscar recursivamente en columnas anidadas (para columnas stacked)
                    if (column.columns && column.columns.length > 0) {
                      const found = searchColumns(column.columns);
                      if (found) return found; // Retorna la primera columna que coincida
                    }
                  }
                  return null; // No se encontró ninguna columna que coincida
                };

                // Buscar la columna usando búsqueda recursiva
                let foundColumn = searchColumns(tableColumns);

                // Fallback: si no se encuentra por nombre, usar índice de columna
                if (!foundColumn) {
                  const columnIndex = target.getAttribute('aria-colindex');
                  if (columnIndex) {
                    const colIndex = parseInt(columnIndex) - 1; // Convertir a 0-based
                    foundColumn = tableColumns[colIndex]; // Acceder directamente por índice
                  }
                }

                return foundColumn;
              };

              const column = findColumn(); // Obtener la configuración de la columna

              if (rowIndex >= 0 && column?.hasTooltip) {
                let content = '';

                if (column.tooltipText) {
                  // Caso 1: Usar texto estático definido en la configuración de la columna
                  content =
                    typeof column.tooltipText === 'string'
                      ? column.tooltipText
                      : String(column.tooltipText);
                } else if (column.tooltipColumn) {
                  // Caso 2: Usar valor dinámico de otra columna de los datos
                  let rowData = null;

                  // Método 1: Obtener datos desde el grid (respeta ordenamiento/filtros/paginación)
                  if (
                    stateRef.current &&
                    typeof stateRef.current.getCurrentViewRecords === 'function'
                  ) {
                    const currentRecords = stateRef.current.getCurrentViewRecords(); // Datos actuales del grid
                    if (currentRecords && currentRecords[rowIndex]) {
                      rowData = currentRecords[rowIndex]; // Usar datos del grid que respetan filtros/orden
                    }
                  }

                  // Método 2: Usar API específica del Grid para obtener datos de la fila
                  if (
                    !rowData &&
                    stateRef.current &&
                    typeof stateRef.current.getRowInfo === 'function'
                  ) {
                    try {
                      const rowInfo = stateRef.current.getRowInfo(target); // Información específica del elemento
                      if (rowInfo && rowInfo.rowData) {
                        rowData = rowInfo.rowData; // Datos exactos de esa celda
                      }
                    } catch (e) {
                      // getRowInfo puede fallar en algunos casos, ignoramos el error
                    }
                  }

                  // Método 3: Fallback a los datos originales (puede no respetar filtros/orden)
                  if (!rowData && dataTable && dataTable[rowIndex]) {
                    rowData = dataTable[rowIndex]; // Último recurso: datos originales
                  }

                  // Extraer el valor específico para el tooltip
                  if (
                    rowData &&
                    rowData[column.tooltipColumn] !== undefined &&
                    rowData[column.tooltipColumn] !== null
                  ) {
                    content = String(rowData[column.tooltipColumn]); // Convertir a string para mostrar
                  }
                }

                // Aplicar el contenido al tooltip o ocultarlo si está vacío
                if (content && content.trim() !== '') {
                  tooltip.content = content; // Establecer el texto del tooltip
                  tooltip.cssClass = 'e-tooltip-wrap'; // Clase CSS para mostrar el tooltip
                } else {
                  tooltip.cssClass = 'hidden-tooltip'; // Clase CSS para ocultar tooltip vacío
                  tooltip.content = ''; // Limpiar contenido
                }
              } else {
                tooltip.cssClass = 'hidden-tooltip';
                tooltip.content = '';
              }
            } catch (error) {
              // Silently handle tooltip errors
              tooltip.cssClass = 'hidden-tooltip';
              tooltip.content = '';
            }
          }
        }
      }}
    >
      {tableColumns?.length > 0 && (
        <GridComponent
          id={id}
          key={key?.toString()}
          showColumnChooser={true}
          height={allowPaging ? undefined : height}
          cssClass={`${compact ? 'grid-compact' : ''}`}
          cellSave={(event: any) => {
            try {
              if (event && event?.name === 'cellSave') {
                const columns = event?.rowData;
                columns[event?.columnName] = event?.value;
                setScrollLeft(stateRef.current?.scrollModule?.content?.scrollLeft);
                if (actionSave) actionSave(columns);
              }
            } catch (error) {
              // Silently handle cellSave errors
            }
          }}
          rowDrop={(event) => {
            try {
              if (onDrop) onDrop(event);
            } catch (error) {
              // Silently handle rowDrop errors
            }
          }}
          enablePersistence={enablePersistence}
          enableVirtualization={
            tableColumns &&
            (tableColumns?.some((column) => column?.allowEditing) || dataTable?.length <= 100)
              ? false
              : enableVirtualization
          }
          locale={currentLocale}
          dataSource={dataTable}
          gridLines={'Horizontal'}
          rowSelected={(event) => {
            select(event);
          }}
          rowDeselected={(event) => {
            deselect(event);
          }}
          ref={(ref) => {
            if (setTableRef) setTableRef(ref);
            changeState(ref);
          }}
          allowPaging={allowPaging}
          pageSettings={{
            pageSize: enableVirtualization ? 100 : pageSize,
            currentPage: initialSelectedRowIndex
              ? Math?.ceil((initialSelectedRowIndex + 1) / (pageSize ?? 15))
              : undefined
          }}
          allowResizing={allowResizing}
          allowRowDragAndDrop={allowRowDragAndDrop}
          allowExcelExport={allowExcelExport}
          allowReordering={allowReordering}
          allowFiltering={allowFiltering}
          filterSettings={{
            columns: initialFilterColumns,
            type: 'Excel',
            enableInfiniteScrolling: true,
            itemsCount: 100
          }}
          allowSorting={allowSorting}
          sortSettings={
            sortSettings
              ? sortSettings
              : tableColumns[0]
                ? {
                    columns: [{ field: tableColumns[0]?.fieldName, direction: 'Ascending' }]
                  }
                : undefined
          }
          toolbarClick={toolbarClick}
          className={toolbar && toolbar?.length > 0 ? 'grid-round-header' : 'grid-round-header'}
          toolbar={toolbar}
          resizeSettings={{
            mode: 'Auto'
          }}
          actionBegin={handleActionBegin}
          selectionSettings={
            allowMultipleSelection
              ? {
                  enableSimpleMultiRowSelection: true,
                  type: 'Multiple',
                  persistSelection: persistSelection
                }
              : {
                  type: 'Single',
                  persistSelection: persistSelection
                }
          }
          selectedRowIndex={
            initialSelectedRowIndex != null ? initialSelectedRowIndex % (pageSize ?? 15) : undefined
          }
          dataBound={() => {
            try {
              const idsToRestore = Array.from(
                new Set(
                  [...(persistedRowIds || []), ...(selectedRowIdsRef.current || [])].filter(
                    (id) => id !== null && id !== undefined
                  )
                )
              );

              if (idsToRestore.length > 0 && stateRef.current?.selectRows) {
                // Usar setTimeout para asegurar que el grid esté completamente renderizado
                setTimeout(() => {
                  try {
                    const currentRecords = stateRef.current?.getCurrentViewRecords?.() || dataTable;
                    const validIndexes: number[] = [];

                    idsToRestore.forEach((persistedId) => {
                      const normalizedId = String(persistedId);
                      const currentIndex = currentRecords.findIndex((row: any) => {
                        const rowId = row?.dataRowTableId;
                        return rowId !== undefined && rowId !== null
                          ? String(rowId) === normalizedId
                          : false;
                      });

                      if (currentIndex >= 0) {
                        validIndexes.push(currentIndex);
                      }
                    });

                    if (validIndexes.length > 0) {
                      stateRef.current.selectRows(validIndexes);
                    }
                  } catch (error) {
                    // Silently handle selection restoration errors
                  } finally {
                    setPersistedRowIds([]);
                  }
                }, 100);
              } else if (persistedRowIds.length > 0) {
                // Si hay IDs persistidos pero no se pueden restaurar, limpiarlos después de un tiempo
                setTimeout(() => {
                  if (persistedRowIds.length > 0) {
                    setPersistedRowIds([]);
                  }
                }, 2000);
              }

              if (stateRef.current?.groupModule && allowGrouping && !initialDataBound) {
                setInitialDataBound(true);
                stateRef.current.groupModule.collapseAll();
              }
            } catch (error) {
              // Silently handle dataBound errors
            }
          }}
          editSettings={{
            allowEditing:
              toolbarOptions?.includes('Edit') ||
              (tableColumns && tableColumns?.some((column) => column?.allowEditing)),
            allowAdding: toolbarOptions?.includes('Add'),
            allowDeleting: toolbarOptions?.includes('Delete'),
            mode: 'Batch'
          }}
          groupSettings={groupSettings}
          allowGrouping={allowGrouping}
          // noRecordsTemplate={usingAdvancedFilters && appIntl().formatMessage({ id: 'common.id' })}
          actionComplete={handleActionComplete}
        >
          <ColumnsDirective>
            {tableColumns?.map((column, i) =>
              column?.columns && column?.columns?.length > 0 ? (
                <ColumnDirective
                  customAttributes={column?.customAttributes}
                  headerText={
                    (column?.staticText ||
                      (column?.headerText
                        ? appIntl()?.formatMessage({ id: column?.headerText })
                        : '')) +
                    (column?.metric
                      ? column?.staticText || column?.headerText
                        ? ' (' +
                          (authSystems[column?.metric] !== undefined
                            ? authSystems[column?.metric]
                            : column?.metric) +
                          ')'
                        : authSystems[column?.metric] !== undefined
                          ? authSystems[column?.metric]
                          : column?.metric
                      : '')
                  }
                  headerTemplate={
                    column?.headerTemplate
                      ? column?.headerTooltip
                        ? `<div id="${id || 'table'}-header-${i}">${column.headerTemplate}</div>`
                        : column.headerTemplate
                      : column?.headerTooltip
                        ? `<div id="${id || 'table'}-header-${i}">${
                            (column?.staticText ||
                              (column?.headerText
                                ? appIntl()?.formatMessage({ id: column?.headerText })
                                : '')) +
                            (column?.metric
                              ? column?.staticText || column?.headerText
                                ? ' (' +
                                  (authSystems[column?.metric] !== undefined
                                    ? authSystems[column?.metric]
                                    : column?.metric) +
                                  ')'
                                : authSystems[column?.metric] !== undefined
                                  ? authSystems[column?.metric]
                                  : column?.metric
                              : '')
                          }</div>`
                        : undefined
                  }
                  key={column?.fieldName || column?.customKey}
                  columns={column?.columns?.map((stackedColumn, j) =>
                    stackedColumn?.columns && stackedColumn?.columns?.length > 0
                      ? {
                          autoFit: true,
                          customAttributes: stackedColumn?.customAttributes,
                          headerText:
                            (stackedColumn?.staticText ||
                              (stackedColumn?.headerText
                                ? appIntl()?.formatMessage({ id: stackedColumn?.headerText })
                                : '')) +
                            (stackedColumn?.metric
                              ? stackedColumn?.staticText || stackedColumn?.headerText
                                ? ' (' +
                                  (authSystems[stackedColumn?.metric] !== undefined
                                    ? authSystems[stackedColumn?.metric]
                                    : stackedColumn?.metric) +
                                  ')'
                                : authSystems[stackedColumn?.metric] !== undefined
                                  ? authSystems[stackedColumn?.metric]
                                  : stackedColumn?.metric
                              : ''),
                          key: stackedColumn?.fieldName || stackedColumn?.customKey,
                          type: stackedColumn.type || 'string',
                          visible: stackedColumn?.hide !== true,
                          columns: stackedColumn?.columns?.map((stackedStackedColumn) => ({
                            autoFit: true,
                            key: stackedStackedColumn?.fieldName,
                            field: stackedStackedColumn?.fieldName,
                            maxWidth: stackedStackedColumn?.maxWidth,
                            minWidth: stackedStackedColumn?.minWidth,
                            // commandsTemplate: stackedColumn.template,
                            headerText:
                              (stackedStackedColumn?.staticText ||
                                (stackedStackedColumn?.headerText
                                  ? appIntl()?.formatMessage({
                                      id: stackedStackedColumn?.headerText
                                    })
                                  : '')) +
                              (stackedStackedColumn?.metric
                                ? stackedColumn?.staticText || stackedStackedColumn?.headerText
                                  ? ' (' +
                                    (authSystems[stackedStackedColumn?.metric] !== undefined
                                      ? authSystems[stackedStackedColumn?.metric]
                                      : stackedStackedColumn?.metric) +
                                    ')'
                                  : authSystems[stackedStackedColumn?.metric] !== undefined
                                    ? authSystems[stackedStackedColumn?.metric]
                                    : stackedStackedColumn?.metric
                                : ''),
                            allowEditing: stackedStackedColumn?.allowEditing ?? false,
                            isPrimaryKey: stackedStackedColumn?.isPrimaryKey,
                            editType:
                              stackedStackedColumn?.editType ??
                              (stackedStackedColumn?.type === 'number'
                                ? 'numericedit'
                                : stackedStackedColumn?.type === 'boolean'
                                  ? 'booleanedit'
                                  : stackedStackedColumn?.type === 'dateTime'
                                    ? 'datetimeedit'
                                    : 'stringedit'),
                            editTemplate: stackedStackedColumn?.editTemplate ?? null,
                            headerTemplate: stackedStackedColumn?.headerTemplate
                              ? stackedStackedColumn?.headerTooltip
                                ? `<div id="${id || 'table'}-header-${i}-${j}-stacked">${
                                    stackedStackedColumn.headerTemplate
                                  }</div>`
                                : stackedStackedColumn.headerTemplate
                              : stackedStackedColumn?.headerTooltip
                                ? `<div id="${id || 'table'}-header-${i}-${j}-stacked">${
                                    (stackedStackedColumn?.staticText ||
                                      (stackedStackedColumn?.headerText
                                        ? appIntl()?.formatMessage({
                                            id: stackedStackedColumn?.headerText
                                          })
                                        : '')) +
                                    (stackedStackedColumn?.metric
                                      ? stackedColumn?.staticText ||
                                        stackedStackedColumn?.headerText
                                        ? ' (' +
                                          (authSystems[stackedStackedColumn?.metric] !== undefined
                                            ? authSystems[stackedStackedColumn?.metric]
                                            : stackedStackedColumn?.metric) +
                                          ')'
                                        : authSystems[stackedStackedColumn?.metric] !== undefined
                                          ? authSystems[stackedStackedColumn?.metric]
                                          : stackedStackedColumn?.metric
                                      : '')
                                  }</div>`
                                : undefined,
                            format: stackedStackedColumn?.formatColumn,
                            type: stackedStackedColumn?.type || 'string',
                            displayAsCheckBox: stackedStackedColumn?.type === 'boolean',
                            template: stackedStackedColumn?.template,
                            // Configuración de atributos CSS para tooltips en columnas stacked anidadas
                            customAttributes: stackedStackedColumn?.customAttributes
                              ? stackedStackedColumn?.customAttributes
                              : {
                                  // Aplicar clase hasTooltip si la columna tiene tooltip configurado
                                  class: stackedStackedColumn?.hasTooltip
                                    ? 'hasTooltip' // Marca la celda para que el tooltip la detecte
                                    : 'withoutTooltip', // Marca la celda como sin tooltip
                                  id: i + '-' + j + '-stacked' // ID único para identificar columnas stacked anidadas
                                },
                            visible: stackedStackedColumn?.hide !== true,
                            freeze: j < (frozenColumns ?? -1) ? 'Left' : undefined,
                            // valueAccessor: (field, data) =>
                            //   stackedColumn.format ? stackedColumn.format(data[field]) : data[field],
                            edit: stackedStackedColumn?.edit,
                            textAlign: stackedStackedColumn?.textAlign
                              ? stackedStackedColumn?.textAlign
                              : 'Left'
                          }))
                        }
                      : {
                          autoFit: true,
                          key: stackedColumn?.fieldName,
                          field: stackedColumn?.fieldName,
                          maxWidth: stackedColumn?.maxWidth,
                          minWidth: stackedColumn?.minWidth,
                          // commandsTemplate: stackedColumn.template,
                          headerText:
                            (stackedColumn?.staticText ||
                              (stackedColumn?.headerText
                                ? appIntl()?.formatMessage({ id: stackedColumn?.headerText })
                                : '')) +
                            (stackedColumn?.metric
                              ? stackedColumn?.staticText || stackedColumn?.headerText
                                ? ' (' +
                                  (authSystems[stackedColumn?.metric] !== undefined
                                    ? authSystems[stackedColumn?.metric]
                                    : stackedColumn?.metric) +
                                  ')'
                                : authSystems[stackedColumn?.metric] !== undefined
                                  ? authSystems[stackedColumn?.metric]
                                  : stackedColumn?.metric
                              : ''),
                          allowEditing: stackedColumn?.allowEditing ?? false,
                          isPrimaryKey: stackedColumn?.isPrimaryKey,
                          editType:
                            stackedColumn?.editType ??
                            (stackedColumn?.type === 'number'
                              ? 'numericedit'
                              : stackedColumn?.type === 'boolean'
                                ? 'booleanedit'
                                : stackedColumn?.type === 'dateTime'
                                  ? 'datetimeedit'
                                  : 'stringedit'),
                          editTemplate: stackedColumn?.editTemplate ?? null,
                          headerTemplate: stackedColumn?.headerTemplate
                            ? stackedColumn?.headerTooltip
                              ? `<div id="${id || 'table'}-header-${i}-${j}">${
                                  stackedColumn.headerTemplate
                                }</div>`
                              : stackedColumn.headerTemplate
                            : stackedColumn?.headerTooltip
                              ? `<div id="${id || 'table'}-header-${i}-${j}">${
                                  (stackedColumn?.staticText ||
                                    (stackedColumn?.headerText
                                      ? appIntl()?.formatMessage({ id: stackedColumn?.headerText })
                                      : '')) +
                                  (stackedColumn?.metric
                                    ? stackedColumn?.staticText || stackedColumn?.headerText
                                      ? ' (' +
                                        (authSystems[stackedColumn?.metric] !== undefined
                                          ? authSystems[stackedColumn?.metric]
                                          : stackedColumn?.metric) +
                                        ')'
                                      : authSystems[stackedColumn?.metric] !== undefined
                                        ? authSystems[stackedColumn?.metric]
                                        : stackedColumn?.metric
                                    : '')
                                }</div>`
                              : undefined,
                          format: stackedColumn?.formatColumn,
                          type: stackedColumn?.type || 'string',
                          displayAsCheckBox: stackedColumn?.type === 'boolean',
                          template: stackedColumn?.template,
                          // Configuración de atributos CSS para tooltips en columnas stacked simples
                          customAttributes: column?.customAttributes
                            ? {
                                class: 'side-borders-columns-subColumns', // Clase especial para bordes
                                id: i + '-' + j
                              }
                            : {
                                // Usar hasTooltip de la columna stacked, no de la columna padre
                                class: stackedColumn?.hasTooltip ? 'hasTooltip' : 'withoutTooltip',
                                id: i + '-' + j // ID único para columnas stacked simples
                              },
                          visible: stackedColumn?.hide !== true,
                          freeze: j < (frozenColumns ?? -1) ? 'Left' : undefined,
                          // valueAccessor: (field, data) =>
                          //   stackedColumn?.format ? stackedColumn.format(data[field]) : data[field],
                          edit: stackedColumn?.edit,
                          textAlign: stackedColumn?.textAlign ? stackedColumn?.textAlign : 'Left'
                        }
                  )}
                ></ColumnDirective>
              ) : (
                <ColumnDirective
                  autoFit={true}
                  key={column?.fieldName ?? column?.customKey}
                  field={column?.fieldName}
                  maxWidth={column?.maxWidth}
                  minWidth={column?.minWidth}
                  // commandsTemplate={column.template}
                  headerText={
                    (column?.staticText ||
                      (column?.headerText
                        ? appIntl()?.formatMessage({ id: column?.headerText })
                        : '')) +
                    (column?.metric
                      ? column?.staticText || column?.headerText
                        ? ' (' +
                          (authSystems[column?.metric] !== undefined
                            ? authSystems[column?.metric]
                            : column?.metric) +
                          ')'
                        : authSystems[column?.metric] !== undefined
                          ? authSystems[column?.metric]
                          : column?.metric
                      : '')
                  }
                  allowEditing={column?.allowEditing ?? false}
                  allowFiltering={
                    disableFilterFields && disableFilterFields.length > 0
                      ? !disableFilterFields.includes(column.fieldName)
                      : allowFiltering
                  }
                  isPrimaryKey={column?.isPrimaryKey}
                  editType={
                    column?.editType ??
                    (column?.type === 'number'
                      ? 'numericedit'
                      : column?.type === 'boolean'
                        ? 'booleanedit'
                        : column?.type === 'dateTime'
                          ? 'datetimeedit'
                          : 'stringedit')
                  }
                  editTemplate={column?.editTemplate ?? null}
                  headerTemplate={
                    column?.headerTemplate
                      ? column?.headerTooltip
                        ? `<div id="${id || 'table'}-header-${i}">${column.headerTemplate}</div>`
                        : column.headerTemplate
                      : column?.headerTooltip
                        ? `<div id="${id || 'table'}-header-${i}">${
                            (column?.staticText ||
                              (column?.headerText
                                ? appIntl()?.formatMessage({ id: column?.headerText })
                                : '')) +
                            (column?.metric
                              ? column?.staticText || column?.headerText
                                ? ' (' +
                                  (authSystems[column?.metric] !== undefined
                                    ? authSystems[column?.metric]
                                    : column?.metric) +
                                  ')'
                                : authSystems[column?.metric] !== undefined
                                  ? authSystems[column?.metric]
                                  : column?.metric
                              : '')
                          }</div>`
                        : undefined
                  }
                  format={column?.formatColumn}
                  type={column?.type || 'string'}
                  displayAsCheckBox={column?.type === 'boolean'}
                  template={column?.template}
                  // Configuración de atributos CSS para tooltips en columnas normales
                  customAttributes={
                    column?.customAttributes
                      ? column?.customAttributes
                      : {
                          // Aplicar clase CSS según si la columna tiene tooltip configurado
                          class: column?.hasTooltip ? 'hasTooltip' : 'withoutTooltip',
                          id: i // ID único para identificar la columna
                        }
                  }
                  visible={column?.hide !== true}
                  freeze={i < (frozenColumns ?? -1) ? 'Left' : undefined}
                  valueAccessor={(field, data) =>
                    column?.format ? column?.format(data[field]) : data[field]
                  }
                  edit={column?.edit}
                  textAlign={column?.textAlign ? column?.textAlign : 'Left'}
                ></ColumnDirective>
              )
            )}
            {tableColumns
              ?.filter((column) => column?.tooltipColumn)
              ?.map((column) => (
                <ColumnDirective
                  customAttributes={column?.customAttributes}
                  autoFit={true}
                  key={column?.tooltipColumn}
                  field={column?.tooltipColumn}
                  template={column?.tooltipTemplate}
                  visible={false}
                ></ColumnDirective>
              ))}
          </ColumnsDirective>
          {frozenColumns === 0 && (
            <AggregatesDirective>
              <AggregateDirective>
                {tableColumns &&
                tableColumns?.filter((column) => column?.aggregateType)?.length > 0 ? (
                  <AggregateColumnsDirective>
                    {tableColumns
                      ?.filter((column) => column?.aggregateType)
                      ?.map((column) => (
                        <AggregateColumnDirective
                          key={column?.fieldName}
                          field={column?.fieldName}
                          type={column?.aggregateType}
                          customAggregate={column?.customAggregateFn || undefined}
                          footerTemplate={(props) => (
                            <span>
                              {appIntl()?.formatMessage({
                                id: column?.aggregateText || 'common.table' + column?.aggregateType
                              })}
                              :{' '}
                              {column?.aggregateType === 'Average'
                                ? props[column?.aggregateType]?.toFixed(2)
                                : props[column?.aggregateType]}
                            </span>
                          )}
                        />
                      ))}
                  </AggregateColumnsDirective>
                ) : (
                  <AggregateColumnsDirective>
                    <AggregateColumnDirective
                      field={tableColumns[0] ? tableColumns[0]?.fieldName : undefined}
                      type={'Count'}
                      footerTemplate={(props) => (
                        <span>
                          {appIntl()?.formatMessage({ id: 'common.tableCount' })}: {props?.Count}
                        </span>
                      )}
                    />
                  </AggregateColumnsDirective>
                )}
              </AggregateDirective>
            </AggregatesDirective>
          )}
          <Inject
            services={[
              Resize,
              Filter,
              Sort,
              Page,
              Toolbar,
              Search,
              Freeze,
              Reorder,
              Aggregate,
              Edit,
              RowDD,
              VirtualScroll,
              ExcelExport,
              ColumnChooser,
              Group
            ]}
          />
        </GridComponent>
      )}

      {/* UncontrolledTooltips para headers */}
      {headerTooltips.map((tooltip) => (
        <UncontrolledTooltip
          key={tooltip.id}
          target={tooltip.id}
          placement="top"
          delay={{ show: 300, hide: 200 }}
        >
          {tooltip.text}
        </UncontrolledTooltip>
      ))}
    </TooltipComponent>
  ) : (
    <Row>
      <Col sm="12" className="grid-spinner">
        <Spinner color="primary" size="lg" />
      </Col>
    </Row>
  );
};

Table.defaultProps = {
  enablePersistence: true,
  omitPersistenceKeys: [
    'selectedRowIndex',
    'searchSettings',
    'filterSettings',
    'scrollPosition',
    'pageSettings'
  ],
  enableVirtualization: false,
  isFetching: false,
  allowSorting: true,
  allowPaging: true,
  pageSize: 15,
  allowRowDragAndDrop: false,
  allowResizing: true,
  allowFiltering: true,
  allowReordering: true,
  sortedColumn: {
    sortedColumnIndex: 0,
    direction: 'Ascending'
  },
  allowMultipleSelection: false,
  frozenColumns: 0,
  height: 450,
  allowDeleting: false,
  allowGrouping: false
};

export default Table;
